import React, { Dispatch, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DialogHeader from '../_parts/DialogHeader';
import { useDispatch, useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../../redux/store';
import { useEnqueueSnackbar } from '../../../../hooks/useEnqueueSnackbar';
import { ToastSeverity } from '../../Toast/Toast';
import DialogFrame from '../_parts/DialogFrame';
import {
    IEmailTemplate,
    MailSendOptionEnum,
    closeShareEmailDialog,
    defaultEmailTemplate,
    getEmailTemplatesForShareEmail,
    getTemplates,
} from '../../../../redux/project/documents/sliceProjectShareEmail';
import { updateSelectedDocuments } from '../../../../redux/project/documents/sliceProjectSelectedDocuments';
import { useParams } from 'react-router-dom';
import { fetchViewDataAndDocuments } from '../../../../util/ProjectUtils';
import {
    fetchImportDocTypes,
    fetchImportViewData,
    fetchImportViews,
} from '../../../../util/ImportUtils';
import {
    ExportConfigEnum,
    getAdvancedExportConfig,
    IncludeInExportType,
} from '../../../../redux/project/documents/sliceProjectAdvancedExport';
import { DialogContent } from '@mui/material';
import { ContentLayout } from '../_parts/ContentLayout';
import Documents from '../_parts/Documents';
import { removeProjectDocumentsInfoItem } from '../../../../redux/project/documents/sliceProjectDocuments';
import ShareEmailContent from './_parts/ShareEmailContent';
import ShareEmailDialogActions from './_parts/ShareEmailDialogActions';
import { IExportAdditionalDocument } from '../_parts/AdvancedExport';
import {
    clearProjectUserList,
    getUserEmailList,
} from '../../../../redux/project/sliceProjectUserList';

const initialMainState: IShareEmailMainState = {
    mailSendOption: MailSendOptionEnum.DownloadLink,
    sendTo: [],
    blindCopyTo: [],
    copyTo: [],
    subject: '',
    emailText: '',
    replyTo: [],
    documentTypeName: '',
    includeInExport: [],
    additionalFiles: [],
    selectedTemplate: defaultEmailTemplate,
};

const initialErrorState: IShareEmailErrorState = {
    mailSendOption: false,
    sendTo: false,
    subject: false,
    replyTo: false,
    documentTypeName: false,
};

const getIncludeInExportFromTemplate = (template: IEmailTemplate): IncludeInExportType[] => {
    const includeInExportOptions: IncludeInExportType[] = [
        ExportConfigEnum.ExportDatamodelOriginal,
        ExportConfigEnum.ExportFileOriginal,
        ExportConfigEnum.ExportNote,
        ExportConfigEnum.ExportProtocol,
    ];
    return includeInExportOptions.filter(item => template[item] === '1');
};

export default () => {
    const { t } = useTranslation('translations', { keyPrefix: 'shareEmailDialog' });
    const dispatch: Dispatch<unknown> = useDispatch();
    const enqueueSnackbar = useEnqueueSnackbar();
    const params = useParams();
    const { replicaId, areaName } = params;

    const { data: documents, loading: loadingInfoDocs } = useSelector(
        (_store: IAppReduxStore) => _store.projectDocuments.info,
    );

    const {
        isDialogOpen,
        isEmailSent,
        loading: loadingEmailTemplates,
    } = useSelector((_store: IAppReduxStore) => _store.projectDocumentsShareEmail);

    const templates = useSelector(getTemplates);

    const userSettings = useSelector((_store: IAppReduxStore) => _store.userProfile);
    const loadingEmailOptions = useSelector(
        (_store: IAppReduxStore) => _store.projectUserList.loading,
    );

    useEffect(() => {
        if (isDialogOpen) {
            dispatch(getEmailTemplatesForShareEmail({ replicaId }));
            dispatch(getAdvancedExportConfig({ replicaId }));
            dispatch(getUserEmailList({ replicaId }));
        }
    }, [isDialogOpen]);

    useEffect(() => {
        if (isEmailSent) {
            dispatch(
                updateSelectedDocuments({
                    replicaId,
                    areaName,
                    documentIds: [],
                }),
            );
            if (areaName === 'import') {
                fetchImportViews(replicaId);
                fetchImportViewData(replicaId);
                fetchImportDocTypes(replicaId);
            } else {
                fetchViewDataAndDocuments(params);
            }
            dispatch(clearProjectUserList());

            onClose();
            enqueueSnackbar(t('toastSuccess'), {
                severity: ToastSeverity.Success,
            });
        }
    }, [isEmailSent]);

    useEffect(() => {
        if (userSettings.data) {
            setMainState({
                ...mainState,
                replyTo: [userSettings.data.email],
            });
        }
    }, [userSettings]);

    const [mainState, setMainState] = useState<IShareEmailMainState>(initialMainState);
    const [showErrorState, setShowErrorState] = useState<IShareEmailErrorState>(initialErrorState);

    const handleChangeTemplate = (template: IEmailTemplate) => {
        setMainState(prevState => ({
            ...prevState,
            selectedTemplate: template,
            mailSendOption: template.mailSendOption,
            sendTo: template.sendTo,
            blindCopyTo: template.blindCopyTo,
            copyTo: template.copyTo,
            subject: template.subject,
            emailText: template.body,
            replyTo: template.replyTo.length ? template.replyTo : [userSettings.data.email],
            documentTypeName: template.FormatFileName,
            includeInExport: getIncludeInExportFromTemplate(template),
            additionalFiles: template.ExportAdditionalDocument,
        }));
    };

    const handleChange = ({ field, value }: { field: string; value: string }) => {
        setShowErrorState({ ...showErrorState, [field]: false });
        setMainState(prevState => ({
            ...prevState,
            [field]: value,
        }));
    };

    const resetState = () => {
        setShowErrorState(initialErrorState);
        setMainState({ ...initialMainState, replyTo: [userSettings.data.email] });
    };

    const onClose = () => {
        resetState();
        dispatch(closeShareEmailDialog());
    };

    const removeDocument = (universalId: string) => {
        dispatch(removeProjectDocumentsInfoItem({ universalId }));
    };

    return (
        <DialogFrame open={isDialogOpen}>
            <DialogHeader onClose={onClose} dialogTitle={t('dialogTitle')} />
            <DialogContent sx={{ pt: 0, overflowX: 'hidden' }}>
                <ContentLayout
                    loading={loadingInfoDocs || loadingEmailTemplates || loadingEmailOptions}
                    documentSelection={
                        <Documents removeDocument={removeDocument} documents={documents} />
                    }
                    main={
                        <ShareEmailContent
                            mainState={mainState}
                            handleChange={handleChange}
                            showErrorState={showErrorState}
                            handleChangeTemplate={handleChangeTemplate}
                            templates={templates}
                        />
                    }
                />
            </DialogContent>

            <ShareEmailDialogActions
                setShowErrorState={setShowErrorState}
                mainState={mainState}
                onClose={onClose}
            />
        </DialogFrame>
    );
};

export interface IShareEmailMainState {
    mailSendOption: MailSendOptionEnum;
    sendTo: string[];
    blindCopyTo: string[];
    copyTo: string[];
    subject: string;
    emailText: string;
    replyTo: string[];
    documentTypeName: string;
    includeInExport: IncludeInExportType[];
    additionalFiles: IExportAdditionalDocument[];
    selectedTemplate: IEmailTemplate;
}

export interface IShareEmailErrorState {
    mailSendOption: boolean;
    sendTo: boolean;
    subject: boolean;
    replyTo: boolean;
    documentTypeName: boolean;
}
